import {
  BillingIntervalSchema,
  PaymentTermSchema,
  PRICING_MODELS,
  PRICING_TEMPLATE_TYPES,
  type PlanInputFormFixedProduct,
  type PricingModel,
  type PricingTemplate,
  type PricingTemplateType,
} from '@vy/types';
import type { Optional, ZodNativeEnum } from '@vy/utils';
import { isNullOrUndefined, parseNumber, withDefault } from '@vy/utils';
import { useCallback } from 'react';
import type { PlanInputFormUsageProduct } from '../../../../../../../libs/types/src/entities/product/plan-input-form-usage-product';
import { useGetPricingTemplates } from '../../../../hooks/requests/use-pricing-template';
import {
  getDefaultCommercialTermsProduct,
  getDefaultFixedProduct,
  getDefaultSubscriptionProduct,
  type ProductBuilderInputs,
} from '../create-plan-page-consts/add-item-button-consts';

export const useGetDefaultProductConfigurations = () => {
  const pricingTemplateResponse = useGetPricingTemplates({
    suspense: true,
  });
  const pricingTemplates = withDefault(pricingTemplateResponse?.pricingTemplates, []);

  const getFixedOneTimeProductConfiguration = useCallback(
    ProductConfigurationFromTemplatesFactory(
      pricingTemplates,
      PRICING_MODELS.FixedOneTime,
      getDefaultFixedProduct,
    ),
    [pricingTemplates],
  );

  const getCommercialTermsProductConfiguration = useCallback(
    ProductConfigurationFromTemplatesFactory(
      pricingTemplates,
      PRICING_MODELS.CommercialTerms,
      getDefaultCommercialTermsProduct,
    ),
    [pricingTemplates],
  );

  const getSubscriptionProductConfiguration = useCallback(
    ProductConfigurationFromTemplatesFactory(
      pricingTemplates,
      PRICING_MODELS.Subscription,
      getDefaultSubscriptionProduct,
    ),
    [pricingTemplates],
  );

  return {
    getFixedOneTimeProductConfiguration,
    getCommercialTermsProductConfiguration,
    getSubscriptionProductConfiguration,
  };
};

function ProductConfigurationFromTemplatesFactory<
  VariantProduct extends PlanInputFormFixedProduct | PlanInputFormUsageProduct,
>(
  pricingTemplates: PricingTemplate[],
  pricingModel: PricingModel,
  productBuilder: (productInputs: ProductBuilderInputs) => VariantProduct,
): () => VariantProduct {
  const paymentTerm = convertTemplateDefaultValueToEnum(
    getTemplateDefaultValue(pricingTemplates, pricingModel, PRICING_TEMPLATE_TYPES.PAYMENT_TERMS),
    PaymentTermSchema,
  );

  const billingInterval = convertTemplateDefaultValueToEnum(
    getTemplateDefaultValue(pricingTemplates, pricingModel, PRICING_TEMPLATE_TYPES.BILLING_INTERVAL),
    BillingIntervalSchema,
  );

  const proRata = convertTemplateDefaultValueToBoolean(
    getTemplateDefaultValue(pricingTemplates, pricingModel, PRICING_TEMPLATE_TYPES.PRORATE),
  );

  const chargingCadence = convertTemplateDefaultValueToNumber(
    getTemplateDefaultValue(pricingTemplates, pricingModel, PRICING_TEMPLATE_TYPES.CADENCE),
  );

  const tierLock = convertTemplateDefaultValueToBoolean(
    getTemplateDefaultValue(pricingTemplates, pricingModel, PRICING_TEMPLATE_TYPES.TIER_RESET),
  );

  return () =>
    productBuilder({
      paymentTerm,
      billingInterval,
      proRata,
      chargingCadence,
      tierLock,
      isCalendarAligned: proRata,
    });
}

function getTemplateDefaultValue(
  pricingTemplates: PricingTemplate[],
  pricingModel: PricingModel,
  templateType: PricingTemplateType,
) {
  const template = pricingTemplates.find(
    (currentTemplate) =>
      currentTemplate.pricingModel === pricingModel && currentTemplate.templateType === templateType,
  );

  return template?.defaultValue;
}

function convertTemplateDefaultValueToEnum<EnumType extends Record<string, string>>(
  defaultValue: Optional<string>,
  schema: ZodNativeEnum<EnumType>,
): EnumType[keyof EnumType] | undefined {
  if (isNullOrUndefined(defaultValue)) {
    return undefined;
  }

  return schema.parse(defaultValue);
}

function convertTemplateDefaultValueToBoolean(defaultValue: Optional<string>): boolean | undefined {
  if (defaultValue === 'true') {
    return true;
  } else if (defaultValue === 'false') {
    return false;
  }

  return undefined;
}

function convertTemplateDefaultValueToNumber(defaultValue: Optional<string>): number | undefined {
  return parseNumber(defaultValue);
}
